import { useState, useEffect, useCallback } from 'react';
import api, { getErrorMessage } from '../services/api';
import Layout from '../components/Layout';

// ── Constants ─────────────────────────────────────────────────────────────────

const USD_TO_INR = 83.5;

const TIERS = [
  { value: 'lms_student', label: 'LMS Student' },
  { value: 'free_public', label: 'Free Public' },
  { value: 'pro_public', label: 'Pro Public' },
];

const PROVIDERS = ['openai', 'anthropic', 'deepseek', 'groq'];

const PROVIDER_MODELS = {
  openai:    ['gpt-4o', 'gpt-4o-mini', 'gpt-4-turbo', 'gpt-3.5-turbo'],
  anthropic: ['claude-opus-4-6', 'claude-sonnet-4-6', 'claude-haiku-4-5-20251001'],
  deepseek:  ['deepseek-chat', 'deepseek-reasoner'],
  groq:      ['llama-3.3-70b-versatile', 'llama-3.1-8b-instant', 'mixtral-8x7b-32768'],
};

const FEATURE_LABELS = {
  study_assistant: 'Study Assistant',
  resume_builder:  'Resume Builder',
  resume_refiner:  'Resume Refiner',
  mock_interview:  'Mock Interview',
  job_alerts:      'Job Alerts',
};

const TIER_COLORS = {
  lms_student: 'bg-blue-100 text-blue-700',
  free_public:  'bg-gray-100 text-gray-700',
  pro_public:   'bg-purple-100 text-purple-700',
};

const TABS = [
  { id: 'access', label: 'Student Access' },
  { id: 'usage',  label: 'Usage & Costs' },
  { id: 'config', label: 'Model Config' },
];

// ── Helpers ───────────────────────────────────────────────────────────────────

const formatDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return isNaN(d.getTime())
    ? '—'
    : d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
};

const toInputDate = (iso) => (iso ? iso.slice(0, 10) : '');

const fmtCostINR = (usd) => `₹${(usd * USD_TO_INR).toFixed(2)}`;

// ── Toast ─────────────────────────────────────────────────────────────────────

const Toast = ({ message, type, onClose }) => {
  useEffect(() => {
    const t = setTimeout(onClose, 4000);
    return () => clearTimeout(t);
  }, [onClose]);

  const cls = {
    success: 'bg-emerald-50 border-emerald-200 text-emerald-800',
    error:   'bg-red-50 border-red-200 text-red-800',
  };
  const iconPath = {
    success: 'M5 13l4 4L19 7',
    error:   'M6 18L18 6M6 6l12 12',
  };
  return (
    <div className={`fixed top-20 right-4 z-50 flex items-center gap-3 px-5 py-3 rounded-xl border shadow-lg animate-slide-down ${cls[type]}`}>
      <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={iconPath[type]} />
      </svg>
      <span className="text-sm font-medium">{message}</span>
      <button onClick={onClose} className="ml-2 opacity-60 hover:opacity-100">
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
  );
};

// ── Shared Modal Shell ────────────────────────────────────────────────────────

const Modal = ({ title, onClose, children, maxW = 'max-w-md' }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
    <div className="fixed inset-0 bg-black/40" onClick={onClose} />
    <div className={`relative bg-white rounded-2xl shadow-xl border border-gray-200 p-6 w-full ${maxW} animate-fade-in`}>
      <div className="flex items-center justify-between mb-5">
        <h3 className="text-lg font-bold text-gray-900">{title}</h3>
        <button onClick={onClose} className="p-1 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
      {children}
    </div>
  </div>
);

// ── Shared form field ─────────────────────────────────────────────────────────

const Field = ({ label, children, hint }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
    {children}
    {hint && <p className="mt-1 text-xs text-gray-400">{hint}</p>}
  </div>
);

const inputCls = 'w-full border border-gray-300 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors';

// ── Skeleton rows ─────────────────────────────────────────────────────────────

const TableSkeleton = ({ cols = 6 }) => (
  <div className="divide-y divide-gray-100">
    {[...Array(4)].map((_, i) => (
      <div key={i} className="flex items-center gap-4 px-6 py-4">
        {[...Array(cols)].map((__, j) => (
          <div key={j} className={`skeleton h-4 ${j === 0 ? 'w-32' : 'w-20'}`} />
        ))}
      </div>
    ))}
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// TAB 1 — Student Access
// ─────────────────────────────────────────────────────────────────────────────

const EMPTY_LIMITS = { tier: 'lms_student', daily_limit: 20, expires_at: '', notes: '' };
const EMPTY_BATCH  = { batch_id: '', tier: 'lms_student', daily_limit: 20, expires_at: '', notes: '' };

const AccessTab = ({ showToast }) => {
  const [accounts, setAccounts]         = useState([]);
  const [loading, setLoading]           = useState(true);
  const [saving, setSaving]             = useState(false);
  const [batches, setBatches]           = useState([]);

  // edit limits modal
  const [editTarget, setEditTarget]     = useState(null);
  const [limitsForm, setLimitsForm]     = useState(EMPTY_LIMITS);

  // batch activate modal
  const [showBatch, setShowBatch]       = useState(false);
  const [batchForm, setBatchForm]       = useState(EMPTY_BATCH);
  const [batchLoading, setBatchLoading] = useState(false);

  // add individual student modal
  const [showAddStudent,  setShowAddStudent]  = useState(false);
  const [students,        setStudents]        = useState([]);
  const [studentsLoading, setStudentsLoading] = useState(false);
  const [addForm,         setAddForm]         = useState({ user_id: '', tier: 'lms_student', daily_limit: 20, expires_at: '' });
  const [addLoading,      setAddLoading]      = useState(false);
  const [studentSearch,   setStudentSearch]   = useState('');

  const loadAccounts = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/ai/admin/accounts');
      setAccounts(res.data);
    } catch (err) {
      showToast(getErrorMessage(err, 'Failed to load accounts'), 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => { loadAccounts(); }, [loadAccounts]);

  // Fetch batches when batch modal opens
  useEffect(() => {
    if (!showBatch || batches.length > 0) return;
    api.get('/api/batches').then((r) => setBatches(r.data || [])).catch(() => {});
  }, [showBatch, batches.length]);

  // Fetch students when add-student modal opens
  useEffect(() => {
    if (!showAddStudent || students.length > 0) return;
    setStudentsLoading(true);
    api.get('/api/users?role=student&per_page=200')
      .then((r) => setStudents(r.data.users || []))
      .catch(() => {})
      .finally(() => setStudentsLoading(false));
  }, [showAddStudent, students.length]);

  // ── Toggle active ────────────────────────────────────────────────────────

  const handleToggle = async (account) => {
    try {
      await api.patch(`/api/ai/admin/accounts/${account.user_id}`, {
        is_active: !account.is_active,
      });
      showToast(
        `CareerAI ${!account.is_active ? 'activated' : 'deactivated'} for ${account.user?.full_name}`,
        'success',
      );
      loadAccounts();
    } catch (err) {
      showToast(getErrorMessage(err, 'Failed to update account'), 'error');
    }
  };

  // ── Edit limits ──────────────────────────────────────────────────────────

  const openEdit = (account) => {
    setEditTarget(account);
    setLimitsForm({
      tier:        account.tier,
      daily_limit: account.study_assistant_daily_limit,
      expires_at:  toInputDate(account.expires_at),
      notes:       account.notes || '',
    });
  };

  const handleSaveLimits = async () => {
    setSaving(true);
    try {
      await api.patch(`/api/ai/admin/accounts/${editTarget.user_id}`, {
        tier:                         limitsForm.tier,
        study_assistant_daily_limit:  Number(limitsForm.daily_limit),
        expires_at:                   limitsForm.expires_at || null,
        notes:                        limitsForm.notes || null,
      });
      showToast('Limits updated successfully', 'success');
      setEditTarget(null);
      loadAccounts();
    } catch (err) {
      showToast(getErrorMessage(err, 'Failed to save limits'), 'error');
    } finally {
      setSaving(false);
    }
  };

  // ── Batch activate ───────────────────────────────────────────────────────

  const handleBatchActivate = async () => {
    if (!batchForm.batch_id) {
      showToast('Please select a batch', 'error');
      return;
    }
    setBatchLoading(true);
    try {
      const res = await api.post(`/api/ai/admin/activate-batch/${batchForm.batch_id}`, {
        tier:                        batchForm.tier,
        study_assistant_daily_limit: Number(batchForm.daily_limit),
        expires_at:                  batchForm.expires_at || null,
        notes:                       batchForm.notes || null,
      });
      const { activated, already_active, total_enrolled } = res.data;
      showToast(
        `Activated ${activated} students (${already_active} already active, ${total_enrolled} total enrolled)`,
        'success',
      );
      setShowBatch(false);
      setBatchForm(EMPTY_BATCH);
      loadAccounts();
    } catch (err) {
      showToast(getErrorMessage(err, 'Batch activation failed'), 'error');
    } finally {
      setBatchLoading(false);
    }
  };

  // ── Add individual student ────────────────────────────────────────────────

  const filteredStudents = studentSearch && !addForm.user_id
    ? students.filter(
        (s) =>
          s.full_name?.toLowerCase().includes(studentSearch.toLowerCase()) ||
          s.email?.toLowerCase().includes(studentSearch.toLowerCase()),
      )
    : [];

  const handleAddStudent = async () => {
    if (!addForm.user_id) {
      showToast('Please select a student', 'error');
      return;
    }
    setAddLoading(true);
    try {
      await api.post('/api/ai/admin/activate', {
        user_id:                     addForm.user_id,
        tier:                        addForm.tier,
        study_assistant_daily_limit: Number(addForm.daily_limit),
        expires_at:                  addForm.expires_at || null,
      });
      showToast('Student activated successfully', 'success');
      setShowAddStudent(false);
      setAddForm({ user_id: '', tier: 'lms_student', daily_limit: 20, expires_at: '' });
      setStudentSearch('');
      loadAccounts();
    } catch (err) {
      showToast(getErrorMessage(err, 'Failed to activate student'), 'error');
    } finally {
      setAddLoading(false);
    }
  };

  // ── Render ───────────────────────────────────────────────────────────────

  return (
    <>
      {/* Action bar */}
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-gray-500">
          {accounts.length} account{accounts.length !== 1 ? 's' : ''} —{' '}
          <span className="text-emerald-600 font-medium">
            {accounts.filter((a) => a.is_active).length} active
          </span>
        </p>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowAddStudent(true)}
            className="flex items-center gap-2 px-4 py-2 bg-primary text-white text-sm font-medium rounded-xl hover:bg-primary/90 active:scale-[0.98] transition-all"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
            </svg>
            Add Student
          </button>
          <button
            onClick={() => setShowBatch(true)}
            className="flex items-center gap-2 px-4 py-2 bg-secondary text-white text-sm font-medium rounded-xl hover:bg-secondary/90 active:scale-[0.98] transition-all"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            Activate by Batch
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-secondary text-white text-xs uppercase tracking-wide">
                <th className="text-left px-6 py-3 font-semibold">Name</th>
                <th className="text-left px-6 py-3 font-semibold">Email</th>
                <th className="text-left px-4 py-3 font-semibold">Tier</th>
                <th className="text-left px-4 py-3 font-semibold">Status</th>
                <th className="text-left px-4 py-3 font-semibold">Daily Limit</th>
                <th className="text-left px-4 py-3 font-semibold">Activated</th>
                <th className="text-left px-4 py-3 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr><td colSpan={7}><TableSkeleton cols={7} /></td></tr>
              ) : accounts.length === 0 ? (
                <tr>
                  <td colSpan={7} className="text-center py-16 text-gray-400">
                    <svg className="w-12 h-12 mx-auto mb-3 text-gray-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                    </svg>
                    <p className="font-medium">No CareerAI accounts yet</p>
                    <p className="text-xs mt-1">Use "Add Student" or "Activate by Batch" to get started</p>
                  </td>
                </tr>
              ) : accounts.map((a) => (
                <tr key={a.id} className="hover:bg-gray-50/60 transition-colors">
                  <td className="px-6 py-3 font-medium text-gray-900">
                    {a.user?.full_name || '—'}
                  </td>
                  <td className="px-6 py-3 text-gray-500">{a.user?.email || '—'}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${TIER_COLORS[a.tier] || 'bg-gray-100 text-gray-700'}`}>
                      {TIERS.find((t) => t.value === a.tier)?.label || a.tier}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${a.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}>
                      {a.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-gray-700 font-medium">{a.study_assistant_daily_limit}/day</td>
                  <td className="px-4 py-3 text-gray-500 whitespace-nowrap">{formatDate(a.activated_at)}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      {a.is_active ? (
                        <button
                          onClick={() => handleToggle(a)}
                          className="px-3 py-1 text-xs font-medium rounded-lg border border-red-200 text-red-600 hover:bg-red-50 transition-colors"
                        >
                          Deactivate
                        </button>
                      ) : (
                        <button
                          onClick={() => handleToggle(a)}
                          className="px-3 py-1 text-xs font-medium rounded-lg bg-emerald-50 text-emerald-700 hover:bg-emerald-100 transition-colors"
                        >
                          Activate
                        </button>
                      )}
                      <button
                        onClick={() => openEdit(a)}
                        className="px-3 py-1 text-xs font-medium rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
                      >
                        Edit
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Edit Limits Modal ── */}
      {editTarget && (
        <Modal title={`Edit Limits — ${editTarget.user?.full_name}`} onClose={() => setEditTarget(null)}>
          <div className="space-y-4">
            <Field label="Tier">
              <select
                value={limitsForm.tier}
                onChange={(e) => setLimitsForm((f) => ({ ...f, tier: e.target.value }))}
                className={inputCls}
              >
                {TIERS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </Field>
            <Field label="Study Assistant Daily Limit" hint="Max messages per day for the study assistant">
              <input
                type="number"
                min={1}
                max={200}
                value={limitsForm.daily_limit}
                onChange={(e) => setLimitsForm((f) => ({ ...f, daily_limit: e.target.value }))}
                className={inputCls}
              />
            </Field>
            <Field label="Expires At" hint="Leave blank for no expiry">
              <input
                type="date"
                value={limitsForm.expires_at}
                onChange={(e) => setLimitsForm((f) => ({ ...f, expires_at: e.target.value }))}
                className={inputCls}
              />
            </Field>
            <Field label="Notes (optional)">
              <textarea
                rows={2}
                value={limitsForm.notes}
                onChange={(e) => setLimitsForm((f) => ({ ...f, notes: e.target.value }))}
                placeholder="Internal notes..."
                className={`${inputCls} resize-none`}
              />
            </Field>
          </div>
          <div className="flex gap-3 justify-end mt-6">
            <button onClick={() => setEditTarget(null)} disabled={saving} className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors">Cancel</button>
            <button onClick={handleSaveLimits} disabled={saving} className="px-4 py-2 text-sm font-medium text-white bg-primary rounded-xl hover:bg-primary/90 transition-colors disabled:opacity-50">
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </Modal>
      )}

      {/* ── Add Student Modal ── */}
      {showAddStudent && (
        <Modal
          title="Add Student"
          onClose={() => { setShowAddStudent(false); setStudentSearch(''); setAddForm({ user_id: '', tier: 'lms_student', daily_limit: 20, expires_at: '' }); }}
        >
          <p className="text-sm text-gray-500 mb-4">
            Activate CareerAI for an individual student.
          </p>
          <div className="space-y-4">
            <Field label="Student">
              <div className="relative">
                <input
                  type="text"
                  value={studentSearch}
                  onChange={(e) => {
                    setStudentSearch(e.target.value);
                    setAddForm((f) => ({ ...f, user_id: '' }));
                  }}
                  placeholder="Search by name or email..."
                  className={inputCls}
                  autoComplete="off"
                />
                {studentsLoading && (
                  <p className="mt-1 text-xs text-gray-400">Loading students...</p>
                )}
                {!studentsLoading && filteredStudents.length > 0 && (
                  <div className="absolute z-10 top-full mt-1 left-0 right-0 bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden max-h-44 overflow-y-auto">
                    {filteredStudents.slice(0, 8).map((s) => (
                      <button
                        key={s.id}
                        type="button"
                        onMouseDown={() => {
                          setAddForm((f) => ({ ...f, user_id: s.id }));
                          setStudentSearch(`${s.full_name} (${s.email})`);
                        }}
                        className="w-full text-left px-3 py-2.5 text-sm hover:bg-gray-50 transition-colors border-b border-gray-50 last:border-0"
                      >
                        <span className="font-medium text-gray-900">{s.full_name}</span>
                        <span className="text-gray-500 ml-2 text-xs">{s.email}</span>
                      </button>
                    ))}
                  </div>
                )}
                {!studentsLoading && studentSearch && !addForm.user_id && filteredStudents.length === 0 && (
                  <p className="mt-1 text-xs text-gray-400">No students found</p>
                )}
              </div>
              {addForm.user_id && (
                <p className="mt-1 text-xs text-emerald-600 font-medium">✓ Student selected</p>
              )}
            </Field>
            <Field label="Tier">
              <select
                value={addForm.tier}
                onChange={(e) => setAddForm((f) => ({ ...f, tier: e.target.value }))}
                className={inputCls}
              >
                {TIERS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </Field>
            <Field label="Daily Limit" hint="Study assistant messages per day">
              <input
                type="number"
                min={1}
                max={200}
                value={addForm.daily_limit}
                onChange={(e) => setAddForm((f) => ({ ...f, daily_limit: e.target.value }))}
                className={inputCls}
              />
            </Field>
            <Field label="Expires At" hint="Leave blank for no expiry">
              <input
                type="date"
                value={addForm.expires_at}
                onChange={(e) => setAddForm((f) => ({ ...f, expires_at: e.target.value }))}
                className={inputCls}
              />
            </Field>
          </div>
          <div className="flex gap-3 justify-end mt-6">
            <button
              onClick={() => { setShowAddStudent(false); setStudentSearch(''); setAddForm({ user_id: '', tier: 'lms_student', daily_limit: 20, expires_at: '' }); }}
              disabled={addLoading}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleAddStudent}
              disabled={addLoading || !addForm.user_id}
              className="px-4 py-2 text-sm font-medium text-white bg-primary rounded-xl hover:bg-primary/90 transition-colors disabled:opacity-50"
            >
              {addLoading ? 'Activating...' : 'Activate Student'}
            </button>
          </div>
        </Modal>
      )}

      {/* ── Batch Activate Modal ── */}
      {showBatch && (
        <Modal title="Activate CareerAI by Batch" onClose={() => setShowBatch(false)}>
          <p className="text-sm text-gray-500 mb-4">
            Activates CareerAI access for all actively enrolled students in the selected batch.
          </p>
          <div className="space-y-4">
            <Field label="Select Batch">
              <select
                value={batchForm.batch_id}
                onChange={(e) => setBatchForm((f) => ({ ...f, batch_id: e.target.value }))}
                className={inputCls}
              >
                <option value="">— Choose a batch —</option>
                {batches.map((b) => (
                  <option key={b.id} value={b.id}>{b.batch_name}</option>
                ))}
              </select>
            </Field>
            <Field label="Tier">
              <select
                value={batchForm.tier}
                onChange={(e) => setBatchForm((f) => ({ ...f, tier: e.target.value }))}
                className={inputCls}
              >
                {TIERS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </Field>
            <Field label="Daily Limit" hint="Study assistant messages per day">
              <input
                type="number"
                min={1}
                max={200}
                value={batchForm.daily_limit}
                onChange={(e) => setBatchForm((f) => ({ ...f, daily_limit: e.target.value }))}
                className={inputCls}
              />
            </Field>
            <Field label="Expires At" hint="Leave blank for no expiry">
              <input
                type="date"
                value={batchForm.expires_at}
                onChange={(e) => setBatchForm((f) => ({ ...f, expires_at: e.target.value }))}
                className={inputCls}
              />
            </Field>
            <Field label="Notes (optional)">
              <textarea
                rows={2}
                value={batchForm.notes}
                onChange={(e) => setBatchForm((f) => ({ ...f, notes: e.target.value }))}
                placeholder="e.g. Batch 2026-A students..."
                className={`${inputCls} resize-none`}
              />
            </Field>
          </div>
          <div className="flex gap-3 justify-end mt-6">
            <button onClick={() => setShowBatch(false)} disabled={batchLoading} className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors">Cancel</button>
            <button onClick={handleBatchActivate} disabled={batchLoading} className="px-4 py-2 text-sm font-medium text-white bg-secondary rounded-xl hover:bg-secondary/90 transition-colors disabled:opacity-50">
              {batchLoading ? 'Activating...' : 'Activate Batch'}
            </button>
          </div>
        </Modal>
      )}
    </>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// TAB 2 — Usage & Costs
// ─────────────────────────────────────────────────────────────────────────────

const UsageTab = ({ showToast }) => {
  const [data, setData]         = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading]   = useState(true);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [statsRes, acctRes] = await Promise.all([
          api.get('/api/ai/admin/usage-stats'),
          api.get('/api/ai/admin/accounts'),
        ]);
        setData(statsRes.data);
        setAccounts(acctRes.data);
      } catch (err) {
        showToast(getErrorMessage(err, 'Failed to load usage stats'), 'error');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [showToast]);

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => <div key={i} className="skeleton h-24 rounded-2xl" />)}
        </div>
        <div className="skeleton h-48 rounded-2xl" />
      </div>
    );
  }

  const stats = data?.stats || [];
  const totalCostUSD = stats.reduce((s, r) => s + (r.estimated_cost_usd || 0), 0);
  const safeFind = (feature) => stats.find((s) => s.feature === feature) || { total_calls: 0, total_tokens: 0, estimated_cost_usd: 0 };
  const studyStat = safeFind('study_assistant');
  const resumeStat = safeFind('resume_builder');
  const activeCount = accounts.filter((a) => a.is_active).length;

  const summaryCards = [
    {
      label: 'Activated Students',
      value: activeCount,
      icon: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z',
      color: 'from-[#003479] to-[#005792]',
    },
    {
      label: 'Study Questions',
      value: studyStat.total_calls.toLocaleString(),
      icon: 'M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
      color: 'from-[#20C997] to-[#1B9AAA]',
    },
    {
      label: 'Resumes Generated',
      value: resumeStat.total_calls.toLocaleString(),
      icon: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z',
      color: 'from-[#1B9AAA] to-[#005792]',
    },
    {
      label: 'Est. Total Cost',
      value: fmtCostINR(totalCostUSD),
      icon: 'M9 8h6m-5 0a3 3 0 110 6H9l3 3m-3-6h6m6 1a9 9 0 11-18 0 9 9 0 0118 0z',
      color: 'from-[#ffb547] to-[#ffd166]',
    },
  ];

  return (
    <div className="space-y-6">
      {/* Summary cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {summaryCards.map((card, i) => (
          <div
            key={card.label}
            style={{ animationDelay: `${i * 60}ms` }}
            className={`animate-fade-in opacity-0 bg-gradient-to-br ${card.color} rounded-2xl p-5 text-white shadow-lg hover:shadow-xl hover:scale-[1.02] transition-all duration-200`}
          >
            <div className="flex items-start justify-between">
              <div>
                <p className="text-2xl font-bold">{card.value}</p>
                <p className="text-xs opacity-90 mt-1">{card.label}</p>
              </div>
              <div className="w-9 h-9 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={card.icon} />
                </svg>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Feature breakdown table */}
      <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100">
          <h3 className="font-semibold text-gray-900">Usage by Feature</h3>
          <p className="text-xs text-gray-400 mt-0.5">All-time aggregate · 1 USD = ₹{USD_TO_INR}</p>
        </div>
        {stats.length === 0 ? (
          <div className="text-center py-12 text-gray-400">
            <p className="font-medium">No usage recorded yet</p>
            <p className="text-xs mt-1">Data will appear once students use CareerAI features</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500 border-b border-gray-100">
                  <th className="text-left px-6 py-3 font-semibold">Feature</th>
                  <th className="text-right px-6 py-3 font-semibold">Total Calls</th>
                  <th className="text-right px-6 py-3 font-semibold">Total Tokens</th>
                  <th className="text-right px-6 py-3 font-semibold">Est. Cost (USD)</th>
                  <th className="text-right px-6 py-3 font-semibold">Est. Cost (INR)</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {stats.map((row) => (
                  <tr key={row.feature} className="hover:bg-gray-50/60">
                    <td className="px-6 py-3 font-medium text-gray-900">
                      {FEATURE_LABELS[row.feature] || row.feature}
                    </td>
                    <td className="px-6 py-3 text-right text-gray-700">{row.total_calls.toLocaleString()}</td>
                    <td className="px-6 py-3 text-right text-gray-700">{row.total_tokens.toLocaleString()}</td>
                    <td className="px-6 py-3 text-right text-gray-500">${row.estimated_cost_usd.toFixed(4)}</td>
                    <td className="px-6 py-3 text-right font-medium text-gray-900">{fmtCostINR(row.estimated_cost_usd)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-gray-50 border-t-2 border-gray-200 font-semibold text-gray-900">
                  <td className="px-6 py-3">Total</td>
                  <td className="px-6 py-3 text-right">{(data?.total_calls || 0).toLocaleString()}</td>
                  <td className="px-6 py-3 text-right">{(data?.total_tokens || 0).toLocaleString()}</td>
                  <td className="px-6 py-3 text-right text-gray-700">${totalCostUSD.toFixed(4)}</td>
                  <td className="px-6 py-3 text-right text-primary">{fmtCostINR(totalCostUSD)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// TAB 3 — Model Configuration
// ─────────────────────────────────────────────────────────────────────────────

const EMPTY_CFG_FORM = { provider: 'openai', model_name: 'gpt-4o-mini', temperature: 0.7, max_tokens: 1000, is_active: true };

const ConfigTab = ({ showToast }) => {
  const [configs, setConfigs]       = useState([]);
  const [loading, setLoading]       = useState(true);
  const [editCfg, setEditCfg]       = useState(null); // { feature, ... }
  const [cfgForm, setCfgForm]       = useState(EMPTY_CFG_FORM);
  const [saving, setSaving]         = useState(false);

  const loadConfigs = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/ai/admin/model-config');
      setConfigs(res.data);
    } catch (err) {
      showToast(getErrorMessage(err, 'Failed to load model configs'), 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => { loadConfigs(); }, [loadConfigs]);

  const openEditCfg = (cfg) => {
    setEditCfg(cfg);
    setCfgForm({
      provider:    cfg.provider,
      model_name:  cfg.model_name,
      temperature: cfg.temperature,
      max_tokens:  cfg.max_tokens,
      is_active:   cfg.is_active,
    });
  };

  // "Add missing" — open modal pre-filled for a feature not yet in DB
  const openNewCfg = (feature) => {
    setEditCfg({ feature, _new: true });
    setCfgForm(EMPTY_CFG_FORM);
  };

  const handleSaveCfg = async () => {
    setSaving(true);
    try {
      await api.put(`/api/ai/admin/model-config/${editCfg.feature}`, {
        provider:    cfgForm.provider,
        model_name:  cfgForm.model_name,
        temperature: Number(cfgForm.temperature),
        max_tokens:  Number(cfgForm.max_tokens),
        is_active:   cfgForm.is_active,
      });
      showToast('Model config updated', 'success');
      setEditCfg(null);
      loadConfigs();
    } catch (err) {
      showToast(getErrorMessage(err, 'Failed to save config'), 'error');
    } finally {
      setSaving(false);
    }
  };

  // Features that should appear in the table
  const modelFeatures = ['study_assistant', 'resume_builder', 'resume_refiner', 'mock_interview'];
  const configMap = Object.fromEntries(configs.map((c) => [c.feature, c]));

  return (
    <>
      {/* Warning banner */}
      <div className="flex gap-3 p-4 bg-amber-50 border border-amber-200 rounded-2xl mb-5">
        <svg className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
        </svg>
        <p className="text-sm text-amber-800">
          <strong>Provider API keys must be set in Railway env vars.</strong>{' '}
          Changing provider requires that provider's API key (e.g. <code className="font-mono text-xs bg-amber-100 px-1 rounded">OPENAI_API_KEY</code>,{' '}
          <code className="font-mono text-xs bg-amber-100 px-1 rounded">ANTHROPIC_API_KEY</code>) to be set on the Railway dashboard before switching.
        </p>
      </div>

      {/* Config table */}
      <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
        {loading ? (
          <TableSkeleton cols={6} />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-secondary text-white text-xs uppercase tracking-wide">
                  <th className="text-left px-6 py-3 font-semibold">Feature</th>
                  <th className="text-left px-4 py-3 font-semibold">Provider</th>
                  <th className="text-left px-4 py-3 font-semibold">Model</th>
                  <th className="text-left px-4 py-3 font-semibold">Temp</th>
                  <th className="text-left px-4 py-3 font-semibold">Max Tokens</th>
                  <th className="text-left px-4 py-3 font-semibold">Status</th>
                  <th className="text-left px-4 py-3 font-semibold">Edit</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {modelFeatures.map((feature) => {
                  const cfg = configMap[feature];
                  return (
                    <tr key={feature} className="hover:bg-gray-50/60">
                      <td className="px-6 py-3 font-medium text-gray-900">{FEATURE_LABELS[feature]}</td>
                      <td className="px-4 py-3 text-gray-700">{cfg?.provider || <span className="text-gray-400">not set</span>}</td>
                      <td className="px-4 py-3 text-gray-700 font-mono text-xs">{cfg?.model_name || <span className="text-gray-400 font-sans text-sm">—</span>}</td>
                      <td className="px-4 py-3 text-gray-700">{cfg?.temperature ?? '—'}</td>
                      <td className="px-4 py-3 text-gray-700">{cfg?.max_tokens?.toLocaleString() ?? '—'}</td>
                      <td className="px-4 py-3">
                        {cfg ? (
                          <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${cfg.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}>
                            {cfg.is_active ? 'Active' : 'Inactive'}
                          </span>
                        ) : (
                          <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
                            Default
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <button
                          onClick={() => cfg ? openEditCfg(cfg) : openNewCfg(feature)}
                          className="px-3 py-1 text-xs font-medium rounded-lg bg-gray-100 text-gray-700 hover:bg-primary/10 hover:text-primary transition-colors"
                        >
                          {cfg ? 'Edit' : 'Configure'}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Edit Config Modal */}
      {editCfg && (
        <Modal
          title={`Configure — ${FEATURE_LABELS[editCfg.feature] || editCfg.feature}`}
          onClose={() => setEditCfg(null)}
        >
          <div className="space-y-4">
            <Field label="Provider">
              <select
                value={cfgForm.provider}
                onChange={(e) => {
                  const provider = e.target.value;
                  const firstModel = PROVIDER_MODELS[provider]?.[0] ?? '';
                  setCfgForm((f) => ({ ...f, provider, model_name: firstModel }));
                }}
                className={inputCls}
              >
                {PROVIDERS.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </Field>
            <Field label="Model" hint={PROVIDER_MODELS[cfgForm.provider] ? undefined : 'Exact model ID for this provider'}>
              {PROVIDER_MODELS[cfgForm.provider] ? (
                <select
                  value={cfgForm.model_name}
                  onChange={(e) => setCfgForm((f) => ({ ...f, model_name: e.target.value }))}
                  className={inputCls}
                >
                  {PROVIDER_MODELS[cfgForm.provider].map((m) => (
                    <option key={m} value={m}>{m}</option>
                  ))}
                </select>
              ) : (
                <input
                  type="text"
                  value={cfgForm.model_name}
                  onChange={(e) => setCfgForm((f) => ({ ...f, model_name: e.target.value }))}
                  placeholder="model-id"
                  className={inputCls}
                />
              )}
            </Field>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Temperature" hint="0 – 2">
                <input
                  type="number"
                  min={0}
                  max={2}
                  step={0.1}
                  value={cfgForm.temperature}
                  onChange={(e) => setCfgForm((f) => ({ ...f, temperature: e.target.value }))}
                  className={inputCls}
                />
              </Field>
              <Field label="Max Tokens">
                <input
                  type="number"
                  min={100}
                  max={8000}
                  step={100}
                  value={cfgForm.max_tokens}
                  onChange={(e) => setCfgForm((f) => ({ ...f, max_tokens: e.target.value }))}
                  className={inputCls}
                />
              </Field>
            </div>
            <Field label="Status">
              <label className="flex items-center gap-3 cursor-pointer">
                <div
                  onClick={() => setCfgForm((f) => ({ ...f, is_active: !f.is_active }))}
                  className={`relative w-10 h-5 rounded-full transition-colors ${cfgForm.is_active ? 'bg-primary' : 'bg-gray-300'}`}
                >
                  <div className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${cfgForm.is_active ? 'translate-x-5' : ''}`} />
                </div>
                <span className="text-sm text-gray-700">{cfgForm.is_active ? 'Active' : 'Inactive'}</span>
              </label>
            </Field>
          </div>
          <div className="flex gap-3 justify-end mt-6">
            <button onClick={() => setEditCfg(null)} disabled={saving} className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors">Cancel</button>
            <button onClick={handleSaveCfg} disabled={saving} className="px-4 py-2 text-sm font-medium text-white bg-primary rounded-xl hover:bg-primary/90 transition-colors disabled:opacity-50">
              {saving ? 'Saving...' : 'Save Config'}
            </button>
          </div>
        </Modal>
      )}
    </>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Main Page
// ─────────────────────────────────────────────────────────────────────────────

const CareerAIAdmin = () => {
  const [activeTab, setActiveTab] = useState('access');
  const [toast, setToast]         = useState(null);

  const showToast = useCallback((message, type = 'success') => {
    setToast({ message, type });
  }, []);

  return (
    <Layout>
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      <div className="space-y-6 animate-fade-in">
        {/* Page header */}
        <div>
          <h1 className="text-2xl font-bold text-gray-900">CareerAI Management</h1>
          <p className="text-sm text-gray-500 mt-1">
            Manage student AI access, monitor usage costs, and configure AI models
          </p>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 bg-gray-100 rounded-2xl p-1 w-fit">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 text-sm font-medium rounded-xl transition-all duration-150 ${
                activeTab === tab.id
                  ? 'bg-white text-secondary shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab content */}
        <div>
          {activeTab === 'access' && <AccessTab showToast={showToast} />}
          {activeTab === 'usage'  && <UsageTab  showToast={showToast} />}
          {activeTab === 'config' && <ConfigTab showToast={showToast} />}
        </div>
      </div>
    </Layout>
  );
};

export default CareerAIAdmin;
